import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Paper,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DOCUMENT } from '../../constants/documentTheme';
import { isDeveloperHost } from '../../utils/developerHost';

const sectionSx = {
  bgcolor: DOCUMENT.surface,
  border: `1px solid ${DOCUMENT.ruleHard}`,
  borderRadius: 0,
  boxShadow: 'none',
  p: { xs: 2.5, md: 3 },
  mb: 3,
};

const codeSx = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontSize: 12.5,
  lineHeight: 1.55,
  bgcolor: DOCUMENT.ivory,
  border: `1px solid ${DOCUMENT.rule}`,
  p: 2,
  overflowX: 'auto',
  whiteSpace: 'pre',
  color: DOCUMENT.ink,
  m: 0,
};

const shotSx = {
  width: '100%',
  display: 'block',
  border: `1px solid ${DOCUMENT.ruleHard}`,
  bgcolor: DOCUMENT.ivory,
};

function CopyCode({ code, copyLabel, copiedLabel }) {
  const [copied, setCopied] = useState(false);
  return (
    <Box sx={{ position: 'relative', mt: 1.5 }}>
      <Box component="pre" sx={codeSx}>{code}</Box>
      <Button
        size="small"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          } catch {
            // ignore
          }
        }}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          minWidth: 0,
          px: 1.25,
          fontSize: 12,
          textTransform: 'none',
          color: DOCUMENT.ink2,
          bgcolor: DOCUMENT.surface,
          border: `1px solid ${DOCUMENT.rule}`,
          borderRadius: 0,
        }}
      >
        {copied ? copiedLabel : copyLabel}
      </Button>
    </Box>
  );
}

const PREVIEW_FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800&q=80&auto=format';

// Verified Unsplash URLs (same set used on partner marketing pages).
const PREVIEW_IMAGES = {
  london: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=800&q=80&auto=format',
  safari: 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=800&q=80&auto=format',
  amalfi: 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=800&q=80&auto=format',
  kyoto: 'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=800&q=80&auto=format',
  kyotoAutumn: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=800&q=80&auto=format',
  lisbon: 'https://images.unsplash.com/photo-1555881400-74d7acaacd8b?w=800&q=80&auto=format',
};

function WidgetPreview({ kind, t }) {
  const isTrips = kind === 'trips';
  const items = isTrips
    ? [
      {
        title: 'London, Stonehenge & Manchester Explorer',
        type: 'City',
        image: PREVIEW_IMAGES.london,
        rows: [
          ['Dates', '16 – 20 Nov 2026'],
          ['Duration', '5 days'],
          ['Travelers', '4 members'],
        ],
      },
      {
        title: "Nabeel's safari",
        type: 'Leisure',
        image: PREVIEW_IMAGES.safari,
        rows: [
          ['Dates', '24 – 31 May 2026'],
          ['Duration', '8 days'],
          ['Travelers', '2 members'],
        ],
      },
      {
        title: 'Amalfi family escape',
        type: 'Family',
        image: PREVIEW_IMAGES.amalfi,
        rows: [
          ['Dates', '10 – 17 Sep 2026'],
          ['Duration', '8 days'],
          ['Travelers', '6 members'],
        ],
      },
    ]
    : [
      {
        title: 'Kyoto Heritage Walk',
        type: 'Cultural',
        image: PREVIEW_IMAGES.kyoto,
        rows: [
          ['Destination', 'Kyoto, Japan'],
          ['Duration', '5 days'],
          ['Price', '$2,890 per person'],
        ],
      },
      {
        title: 'Kyoto autumn week',
        type: 'Cultural',
        image: PREVIEW_IMAGES.kyotoAutumn,
        rows: [
          ['Destination', 'Kyoto, Japan'],
          ['Duration', '5 days'],
          ['Price', '$6,200 per person'],
        ],
      },
      {
        title: 'Lisbon food & wine',
        type: 'Culinary',
        image: PREVIEW_IMAGES.lisbon,
        rows: [
          ['Destination', 'Lisbon, Portugal'],
          ['Duration', '5 days'],
          ['Price', '$3,450 per person'],
        ],
      },
    ];

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        border: `1px solid ${DOCUMENT.ruleHard}`,
        bgcolor: DOCUMENT.ivory,
      }}
      data-testid={`widget-preview-${kind}`}
    >
      <Typography sx={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: DOCUMENT.ink3, mb: 0.75 }}>
        {t('developerPortal.widgets.livePreview')}
      </Typography>
      <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3, mb: 1.5, maxWidth: '62ch', lineHeight: 1.55 }}>
        {t('developerPortal.widgets.previewNote')}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: 1.5,
        }}
      >
        {items.map((item) => (
          <Box
            key={item.title}
            sx={{
              border: `1px solid ${DOCUMENT.rule}`,
              bgcolor: DOCUMENT.surface,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              minHeight: 280,
            }}
          >
            <Box
              sx={{
                position: 'relative',
                height: 110,
                bgcolor: DOCUMENT.oxblood,
                overflow: 'hidden',
              }}
            >
              <Box
                component="img"
                src={item.image}
                alt=""
                onError={(event) => {
                  if (event.currentTarget.src !== PREVIEW_FALLBACK_IMAGE) {
                    event.currentTarget.src = PREVIEW_FALLBACK_IMAGE;
                    return;
                  }
                  event.currentTarget.style.display = 'none';
                }}
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(to top, rgba(18,5,8,0.72) 0%, rgba(18,5,8,0.18) 48%, transparent 74%)',
                  pointerEvents: 'none',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 10,
                  left: 10,
                  zIndex: 1,
                  display: 'inline-block',
                  bgcolor: '#C9A961',
                  color: '#2A070C',
                  fontSize: 10,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  px: 0.9,
                  py: 0.35,
                }}
              >
                {item.type}
              </Box>
            </Box>
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
              <Typography sx={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#C9A961' }}>
                {item.type}
              </Typography>
              <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18, lineHeight: 1.3 }}>
                {item.title}
              </Typography>
              <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, mt: 0.5 }}>
                {item.rows.map(([label, value]) => (
                  <Box
                    component="li"
                    key={label}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 1,
                      py: 0.75,
                      borderTop: `1px solid ${DOCUMENT.rule}`,
                      fontSize: 12.5,
                      color: DOCUMENT.ink2,
                    }}
                  >
                    <span>{label}</span>
                    <Box component="span" sx={{ fontWeight: 600, color: DOCUMENT.ink, textAlign: 'right' }}>{value}</Box>
                  </Box>
                ))}
              </Box>
              <Box
                sx={{
                  alignSelf: 'flex-end',
                  mt: 'auto',
                  bgcolor: DOCUMENT.oxblood,
                  color: DOCUMENT.ivory,
                  px: 1.5,
                  py: 0.75,
                  fontSize: 11,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                }}
              >
                {isTrips ? t('developerPortal.widgets.ctaViewTrip') : t('developerPortal.widgets.ctaBook')}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

const DeveloperWidgets = () => {
  const { t } = useTranslation();
  const copyLabel = t('developerPortal.docs.copy');
  const copiedLabel = t('developerPortal.docs.copied');
  const widgetHost = isDeveloperHost()
    ? (typeof window !== 'undefined' ? window.location.origin : 'https://developer.dejavunomad.com')
    : 'https://developer.dejavunomad.com';

  const packagesSnippet = `<script src="${widgetHost}/widget.js"></script>
<div data-dvn-catalog="YOUR_CATALOG_TOKEN"></div>`;

  const tripsSnippet = `<script src="${widgetHost}/widget.js"></script>
<div data-dvn-trips="YOUR_CATALOG_TOKEN"></div>`;

  const bothSnippet = `<script src="${widgetHost}/widget.js"></script>

<!-- Only this partner's published packages -->
<div data-dvn-catalog="YOUR_CATALOG_TOKEN"></div>

<!-- Only this partner's managed trips -->
<div data-dvn-trips="YOUR_CATALOG_TOKEN"></div>`;

  const advancedSnippet = `<script
  src="${widgetHost}/widget.js"
  data-api="https://api.dejavunomad.com"
></script>
<div
  data-dvn-catalog="YOUR_CATALOG_TOKEN"
  data-dvn-theme="light"
></div>`;

  const themeSnippet = `<script src="${widgetHost}/widget.js"></script>
<div
  class="my-site-packages"
  data-dvn-catalog="YOUR_CATALOG_TOKEN"
  data-dvn-theme="dark"
></div>`;

  const cssVarsSnippet = `<script src="${widgetHost}/widget.js"></script>
<div
  data-dvn-catalog="YOUR_CATALOG_TOKEN"
  style="
    --dvn-color-cta-bg: #004080;
    --dvn-color-accent: #d4af37;
    --dvn-grid-min: 320px;
    --dvn-card-radius: 8px;
  "
></div>`;

  const cssOverrideSnippet = `<style>
  /* Scope overrides to your wrapper class */
  .my-site-packages .dvn-card {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  }
  .my-site-packages .dvn-card a.dvn-cta {
    border-radius: 999px;
    padding: 12px 20px;
  }
  .my-site-packages .dvn-widget-grid {
    gap: 28px;
  }
</style>
<script src="${widgetHost}/widget.js"></script>
<div class="my-site-packages" data-dvn-catalog="YOUR_CATALOG_TOKEN"></div>`;

  const cssVarsTable = [
    ['--dvn-color-cta-bg', t('developerPortal.widgets.varCtaBg')],
    ['--dvn-color-cta-text', t('developerPortal.widgets.varCtaText')],
    ['--dvn-color-accent', t('developerPortal.widgets.varAccent')],
    ['--dvn-color-surface', t('developerPortal.widgets.varSurface')],
    ['--dvn-color-border', t('developerPortal.widgets.varBorder')],
    ['--dvn-color-ink', t('developerPortal.widgets.varInk')],
    ['--dvn-grid-min', t('developerPortal.widgets.varGridMin')],
    ['--dvn-grid-gap', t('developerPortal.widgets.varGridGap')],
    ['--dvn-card-radius', t('developerPortal.widgets.varRadius')],
    ['--dvn-font-display', t('developerPortal.widgets.varFontDisplay')],
    ['--dvn-font-ui', t('developerPortal.widgets.varFontUi')],
  ];

  return (
    <Box>
      <Typography component="h1" sx={{ fontFamily: DOCUMENT.display, fontSize: { xs: 28, md: 36 }, mb: 1 }}>
        {t('developerPortal.widgets.title')}
      </Typography>
      <Typography sx={{ color: DOCUMENT.ink2, mb: 3, maxWidth: '68ch', lineHeight: 1.65 }}>
        {t('developerPortal.widgets.subtitle')}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        <Chip label={t('developerPortal.widgets.badgeNoApiKey')} sx={{ borderRadius: 0 }} color="success" variant="outlined" />
        <Chip label={t('developerPortal.widgets.badgeAgency')} sx={{ borderRadius: 0 }} />
        <Chip label={t('developerPortal.widgets.badgeListOnly')} sx={{ borderRadius: 0 }} />
      </Box>

      <Paper elevation={0} sx={sectionSx}>
        <Typography component="h2" sx={{ fontFamily: DOCUMENT.display, fontSize: 22, mb: 1 }}>
          {t('developerPortal.widgets.scopeTitle')}
        </Typography>
        <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14.5, lineHeight: 1.65, maxWidth: '68ch', mb: 1.5 }}>
          {t('developerPortal.widgets.scopeBody')}
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5, color: DOCUMENT.ink2, fontSize: 14.5, lineHeight: 1.75, maxWidth: '68ch' }}>
          <li>{t('developerPortal.widgets.scopeItem1')}</li>
          <li>{t('developerPortal.widgets.scopeItem2')}</li>
          <li>{t('developerPortal.widgets.scopeItem3')}</li>
        </Box>
      </Paper>

      <Paper elevation={0} sx={sectionSx}>
        <Typography component="h2" sx={{ fontFamily: DOCUMENT.display, fontSize: 22, mb: 1 }}>
          {t('developerPortal.widgets.howTitle')}
        </Typography>
        <Box component="ol" sx={{ m: 0, pl: 2.5, color: DOCUMENT.ink2, fontSize: 14.5, lineHeight: 1.75, maxWidth: '68ch' }}>
          <li>{t('developerPortal.widgets.howStep1')}</li>
          <li>{t('developerPortal.widgets.howStep2')}</li>
          <li>{t('developerPortal.widgets.howStep3')}</li>
        </Box>
      </Paper>

      <Paper elevation={0} sx={sectionSx} id="packages">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Typography component="h2" sx={{ fontFamily: DOCUMENT.display, fontSize: 24 }}>
            {t('developerPortal.widgets.packagesTitle')}
          </Typography>
          <Chip label="data-dvn-catalog" size="small" sx={{ borderRadius: 0, fontFamily: 'monospace' }} />
        </Box>
        <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14.5, lineHeight: 1.65, mb: 2, maxWidth: '68ch' }}>
          {t('developerPortal.widgets.packagesBody')}
        </Typography>

        <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18, mb: 1 }}>
          {t('developerPortal.widgets.screenshotLabel')}
        </Typography>
        <Box
          component="img"
          src="/marketing/widget-packages-list.jpg"
          alt={t('developerPortal.widgets.packagesShotAlt')}
          sx={shotSx}
        />
        <Typography sx={{ color: DOCUMENT.ink3, fontSize: 13, mt: 1, mb: 2 }}>
          {t('developerPortal.widgets.packagesShotCaption')}
        </Typography>

        <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18, mb: 0.5 }}>
          {t('developerPortal.widgets.embedCode')}
        </Typography>
        <CopyCode code={packagesSnippet} copyLabel={copyLabel} copiedLabel={copiedLabel} />
        <WidgetPreview kind="packages" t={t} />
      </Paper>

      <Paper elevation={0} sx={sectionSx} id="trips">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Typography component="h2" sx={{ fontFamily: DOCUMENT.display, fontSize: 24 }}>
            {t('developerPortal.widgets.tripsTitle')}
          </Typography>
          <Chip label="data-dvn-trips" size="small" sx={{ borderRadius: 0, fontFamily: 'monospace' }} />
        </Box>
        <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14.5, lineHeight: 1.65, mb: 2, maxWidth: '68ch' }}>
          {t('developerPortal.widgets.tripsBody')}
        </Typography>

        <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18, mb: 1 }}>
          {t('developerPortal.widgets.screenshotLabel')}
        </Typography>
        <Box
          component="img"
          src="/marketing/widget-trips-list.jpg"
          alt={t('developerPortal.widgets.tripsShotAlt')}
          sx={shotSx}
        />
        <Typography sx={{ color: DOCUMENT.ink3, fontSize: 13, mt: 1, mb: 2 }}>
          {t('developerPortal.widgets.tripsShotCaption')}
        </Typography>

        <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18, mb: 0.5 }}>
          {t('developerPortal.widgets.embedCode')}
        </Typography>
        <CopyCode code={tripsSnippet} copyLabel={copyLabel} copiedLabel={copiedLabel} />
        <WidgetPreview kind="trips" t={t} />
      </Paper>

      <Paper elevation={0} sx={sectionSx}>
        <Typography component="h2" sx={{ fontFamily: DOCUMENT.display, fontSize: 22, mb: 1 }}>
          {t('developerPortal.widgets.bothTitle')}
        </Typography>
        <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14.5, lineHeight: 1.65, mb: 1.5, maxWidth: '68ch' }}>
          {t('developerPortal.widgets.bothBody')}
        </Typography>
        <CopyCode code={bothSnippet} copyLabel={copyLabel} copiedLabel={copiedLabel} />
      </Paper>

      <Paper elevation={0} sx={sectionSx} id="styling">
        <Typography component="h2" sx={{ fontFamily: DOCUMENT.display, fontSize: 22, mb: 1 }}>
          {t('developerPortal.widgets.stylingTitle')}
        </Typography>
        <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14.5, lineHeight: 1.65, mb: 2, maxWidth: '68ch' }}>
          {t('developerPortal.widgets.stylingBody')}
        </Typography>

        <Typography component="h3" sx={{ fontFamily: DOCUMENT.display, fontSize: 18, mb: 0.5 }}>
          {t('developerPortal.widgets.stylingThemeTitle')}
        </Typography>
        <Typography sx={{ color: DOCUMENT.ink3, fontSize: 13.5, mb: 1, maxWidth: '68ch', lineHeight: 1.6 }}>
          {t('developerPortal.widgets.stylingThemeBody')}
        </Typography>
        <CopyCode code={themeSnippet} copyLabel={copyLabel} copiedLabel={copiedLabel} />

        <Typography component="h3" sx={{ fontFamily: DOCUMENT.display, fontSize: 18, mb: 0.5, mt: 3 }}>
          {t('developerPortal.widgets.stylingVarsTitle')}
        </Typography>
        <Typography sx={{ color: DOCUMENT.ink3, fontSize: 13.5, mb: 1, maxWidth: '68ch', lineHeight: 1.6 }}>
          {t('developerPortal.widgets.stylingVarsBody')}
        </Typography>
        <CopyCode code={cssVarsSnippet} copyLabel={copyLabel} copiedLabel={copiedLabel} />
        <Box
          component="ul"
          sx={{
            listStyle: 'none',
            m: '16px 0 0',
            p: 0,
            border: `1px solid ${DOCUMENT.rule}`,
            bgcolor: DOCUMENT.ivory,
            fontSize: 13,
          }}
        >
          {cssVarsTable.map(([name, desc]) => (
            <Box
              component="li"
              key={name}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '220px 1fr' },
                gap: 1,
                px: 2,
                py: 1.1,
                borderTop: `1px solid ${DOCUMENT.rule}`,
                '&:first-of-type': { borderTop: 0 },
              }}
            >
              <Box component="code" sx={{ fontFamily: 'monospace', fontSize: 12.5, color: DOCUMENT.oxblood }}>
                {name}
              </Box>
              <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2, m: 0 }}>{desc}</Typography>
            </Box>
          ))}
        </Box>

        <Typography component="h3" sx={{ fontFamily: DOCUMENT.display, fontSize: 18, mb: 0.5, mt: 3 }}>
          {t('developerPortal.widgets.stylingCssTitle')}
        </Typography>
        <Typography sx={{ color: DOCUMENT.ink3, fontSize: 13.5, mb: 1, maxWidth: '68ch', lineHeight: 1.6 }}>
          {t('developerPortal.widgets.stylingCssBody')}
        </Typography>
        <CopyCode code={cssOverrideSnippet} copyLabel={copyLabel} copiedLabel={copiedLabel} />

        <Typography sx={{ color: DOCUMENT.ink3, fontSize: 13.5, mt: 2, lineHeight: 1.6, maxWidth: '68ch' }}>
          {t('developerPortal.widgets.stylingClasses')}
        </Typography>
      </Paper>

      <Paper elevation={0} sx={sectionSx}>
        <Typography component="h2" sx={{ fontFamily: DOCUMENT.display, fontSize: 22, mb: 1 }}>
          {t('developerPortal.widgets.advancedTitle')}
        </Typography>
        <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14.5, lineHeight: 1.65, mb: 1.5, maxWidth: '68ch' }}>
          {t('developerPortal.widgets.advancedBody')}
        </Typography>
        <CopyCode code={advancedSnippet} copyLabel={copyLabel} copiedLabel={copiedLabel} />
        <Typography sx={{ color: DOCUMENT.ink3, fontSize: 13.5, mt: 2, lineHeight: 1.6 }}>
          {t('developerPortal.widgets.security')}
        </Typography>
      </Paper>
    </Box>
  );
};

export default DeveloperWidgets;
